import {
    createContext,
    type Context,
    type FC,
    type PropsWithChildren,
    useCallback,
    useContext,
} from "react";

export interface PluginSurfaceEnvironment {
    /** The isolated tree that owns the current plugin UI. */
    shadowRoot: ShadowRoot;
    /** The in-surface target for dropdowns, tooltips, and other overlays. */
    portalRoot: HTMLElement;
}

const PLUGIN_SURFACE_CONTEXT_KEY = Symbol.for(
    "magic-md.plugin-sdk.plugin-surface-context",
);

type PluginSurfaceContextGlobal = typeof globalThis & {
    [PLUGIN_SURFACE_CONTEXT_KEY]?: Context<PluginSurfaceEnvironment | null>;
};

/**
 * Standalone plugin UI bundles include their own plugin-sdk module. Keep the
 * host Provider and bundled hooks on one Context identity within the page.
 */
function getSharedPluginSurfaceContext(): Context<PluginSurfaceEnvironment | null> {
    const globalScope = globalThis as PluginSurfaceContextGlobal;
    const existing = globalScope[PLUGIN_SURFACE_CONTEXT_KEY];
    if (existing) return existing;

    const created = createContext<PluginSurfaceEnvironment | null>(null);
    globalScope[PLUGIN_SURFACE_CONTEXT_KEY] = created;
    return created;
}

const PluginSurfaceContext = getSharedPluginSurfaceContext();

export interface PluginSurfaceProviderProps extends PropsWithChildren {
    value: PluginSurfaceEnvironment;
}

export const PluginSurfaceProvider: FC<PluginSurfaceProviderProps> = ({
    value,
    children,
}) => (
    <PluginSurfaceContext.Provider value={value}>
        {children}
    </PluginSurfaceContext.Provider>
);

/** Returns the current isolated plugin surface, if called by plugin UI. */
export function usePluginSurface(): PluginSurfaceEnvironment | null {
    return useContext(PluginSurfaceContext);
}

/**
 * Returns the current isolated plugin surface and fails closed when plugin UI
 * attempts a host-coupled operation outside the mandatory surface boundary.
 */
export function useRequiredPluginSurface(): PluginSurfaceEnvironment {
    const surface = usePluginSurface();
    if (!surface) {
        throw new Error(
            "Plugin UI must render inside the Magic MD PluginSurface",
        );
    }
    return surface;
}

/**
 * Creates a viewport-sized interaction layer inside the plugin ShadowRoot.
 *
 * Resize and drag operations use this instead of mutating document.body. The
 * returned disposer must be called when the interaction ends.
 */
export function usePluginInteractionShield(): (cursor: string) => () => void {
    const surface = usePluginSurface();

    return useCallback((cursor: string) => {
        if (!surface) return () => undefined;

        const shield = surface.shadowRoot.ownerDocument.createElement("div");
        shield.dataset.magicMdPluginInteractionShield = "";
        Object.assign(shield.style, {
            position: "fixed",
            inset: "0",
            zIndex: "2147483647",
            cursor,
            userSelect: "none",
            background: "transparent",
        });
        surface.portalRoot.append(shield);

        return () => shield.remove();
    }, [surface]);
}
